import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";

@Component({
  selector: "app-typeahead",
  templateUrl: "./typeahead.component.html",
  styleUrls: ["./typeahead.component.css"],
})
export class TypeaheadComponent implements OnInit, OnChanges, OnDestroy {
  /** force=true: botão Buscar. force=false: busca automática após pausa na digitação. */
  @Input() onType: (value: string, force?: boolean) => void = () => {};
  @Input() disabled: boolean = false;
  @Input() options: string[] = [];
  @Input() onOptionClick: (value: string) => void = () => {};
  @Input() loading: boolean = false;

  inputValue: string = "";
  showDropdown: boolean = false;

  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;

  private readonly minChars = 2;
  /** Pausa após parar de digitar antes de disparar a busca automática (ms). */
  private readonly debounceDigitacaoMs = 1000;
  private readonly input$ = new Subject<string>();
  private subscription?: Subscription;
  private restoreFocusAfterSearch = false;

  ngOnInit(): void {
    this.subscription = this.input$
      .pipe(
        map((v) => (v ?? "").trim()),
        debounceTime(this.debounceDigitacaoMs),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        this.emitSearchIfEligible(value, false);
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading || changes.options) {
      this.showDropdown = this.loading || this.options.length > 0;

      if (
        this.restoreFocusAfterSearch &&
        !this.loading &&
        this.options.length > 0
      ) {
        this.focusInput();
        this.restoreFocusAfterSearch = false;
      }
    }
  }

  handleInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.inputValue = value;
    this.input$.next(value);
    if ((value ?? "").trim().length === 0) {
      this.showDropdown = false;
    }
  }

  search(): void {
    this.emitSearchIfEligible((this.inputValue ?? "").trim(), true);
    this.focusInput();
  }

  private emitSearchIfEligible(value: string, force: boolean): void {
    if (!value) {
      this.onType("", false);
      this.showDropdown = false;
      return;
    }

    if (!force && value.length < this.minChars) {
      this.showDropdown = false;
      return;
    }

    this.onType(value, force);
    this.restoreFocusAfterSearch = true;
    this.showDropdown = this.loading || this.options.length > 0;
  }

  handleOptionClick(option: any): void {
    this.onOptionClick(option);
    this.inputValue = "";
    this.showDropdown = false;
    this.focusInput();
  }

  hideDropdown(): void {
    setTimeout(() => {
      this.showDropdown = false;
      this.restoreFocusAfterSearch = false;
    }, 200);
  }

  close(): void {
    this.options = [];
    this.inputValue = "";
  }

  private focusInput(): void {
    setTimeout(() => {
      if (!this.disabled) {
        this.searchInput?.nativeElement.focus();
      }
    });
  }
}
